import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { useChat } from '@/contexts/ChatContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Calendar,
  Loader2,
  MapPin,
  NotebookPen,
  Phone,
  Video,
} from 'lucide-react';
import { appointmentService, getLocalizedValue, normalizeImageValue } from '@/services/api';
import { parseApiDateTime } from '@/lib/dateTime';
import { Link, useNavigate } from 'react-router-dom';
import { addMinutes, format, isWithinInterval, subMinutes } from 'date-fns';
import { ar, enUS } from 'date-fns/locale';
import { toast } from 'sonner';

const STATUS_BY_ID = {
  1: 'pending',
  2: 'accepted',
  3: 'in_clinic',
  4: 'done',
  5: 'cancel',
  6: 'cancel',
  7: 'pending',
};

const FILTERS = [
  'all',
  'pending',
  'accepted',
  'in_clinic',
  'done',
  'cancel',
  'unfinished',
];

const normalizeAppointmentState = (state, statusId) => {
  const raw = String(state || STATUS_BY_ID[statusId] || 'pending').trim().toLowerCase();

  if (['accepted', 'confirmed'].includes(raw)) return 'accepted';
  if (['pending', 'draft', 'request', 'waiting'].includes(raw)) return 'pending';
  if (['in_clinic', 'in_progress'].includes(raw)) return 'in_clinic';
  if (['done', 'completed'].includes(raw)) return 'done';
  if (['cancel', 'cancelled', 'canceled', 'rejected', 'no_show'].includes(raw)) return 'cancel';
  if (raw === 'unfinished') return 'unfinished';

  return raw;
};

const getStatusPresentation = (state, lang) => {
  const normalized = normalizeAppointmentState(state);

  const map = {
    pending: {
      label: lang === 'ar' ? 'قيد الانتظار' : 'Pending',
      tint: 'text-amber-500 dark:text-amber-300',
      badge: 'border border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-400/20 dark:bg-amber-500/12 dark:text-amber-200',
      panel: 'bg-amber-50 dark:bg-amber-500/10',
    },
    accepted: {
      label: lang === 'ar' ? 'مقبول' : 'Accepted',
      tint: 'text-emerald-600 dark:text-emerald-300',
      badge: 'border border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-400/20 dark:bg-emerald-500/12 dark:text-emerald-200',
      panel: 'bg-emerald-50 dark:bg-emerald-500/10',
    },
    in_clinic: {
      label: lang === 'ar' ? 'داخل العيادة' : 'In The Clinic',
      tint: 'text-fuchsia-600 dark:text-fuchsia-300',
      badge: 'border border-fuchsia-200 bg-fuchsia-50 text-fuchsia-700 dark:border-fuchsia-400/20 dark:bg-fuchsia-500/12 dark:text-fuchsia-200',
      panel: 'bg-fuchsia-50 dark:bg-fuchsia-500/10',
    },
    done: {
      label: lang === 'ar' ? 'منتهي' : 'Done',
      tint: 'text-sky-600 dark:text-sky-300',
      badge: 'border border-sky-200 bg-sky-50 text-sky-700 dark:border-sky-400/20 dark:bg-sky-500/12 dark:text-sky-200',
      panel: 'bg-sky-50 dark:bg-sky-500/10',
    },
    cancel: {
      label: lang === 'ar' ? 'ملغي' : 'Cancelled',
      tint: 'text-rose-600 dark:text-rose-300',
      badge: 'border border-rose-200 bg-rose-50 text-rose-700 dark:border-rose-400/20 dark:bg-rose-500/12 dark:text-rose-200',
      panel: 'bg-rose-50 dark:bg-rose-500/10',
    },
    unfinished: {
      label: lang === 'ar' ? 'غير مكتمل' : 'Unfinished',
      tint: 'text-orange-600 dark:text-orange-300',
      badge: 'border border-orange-200 bg-orange-50 text-orange-700 dark:border-orange-400/20 dark:bg-orange-500/12 dark:text-orange-200',
      panel: 'bg-orange-50 dark:bg-orange-500/10',
    },
  };

  return map[normalized] || map.pending;
};

const AppointmentsPage = () => {
  const { t, i18n } = useTranslation();
  const { isAuthenticated, loading: authLoading } = useAuth();
  const { startConversation, startCall } = useChat();
  const navigate = useNavigate();
  const [appointments, setAppointments] = useState([]);
  const [realtimeRefreshKey, setRealtimeRefreshKey] = useState(0);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState('all');
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [cancelling, setCancelling] = useState(false);
  const lang = i18n.language || 'ar';
  const isCancelInProgress = useRef(false);

  const locale = lang === 'ar' ? ar : enUS;

  const canJoinCall = (appointment) => {
    if (!appointment.online) return false;

    const now = new Date();
    const appointmentTime = parseApiDateTime(appointment.appointment_at);
    if (!appointmentTime) return false;

    const windowStart = subMinutes(appointmentTime, 15);
    const windowEnd = addMinutes(appointmentTime, 30);

    return isWithinInterval(now, { start: windowStart, end: windowEnd });
  };

  const handleJoinCall = async (appointment) => {
    try {
      await startConversation(appointment.doctor.id, 'doctor');
      navigate('/messages');
      setTimeout(() => {
        startCall('video');
      }, 1000);
    } catch (error) {
      console.error('Failed to join call:', error);
      toast.error(lang === 'ar' ? 'فشل في الانضمام للمكالمة' : 'Failed to join call');
    }
  };

  const handleCancelClick = (appointment) => {
    setSelectedAppointment(appointment);
    setCancelDialogOpen(true);
  };

  const handleConfirmCancel = async () => {
    if (!selectedAppointment || isCancelInProgress.current) return;

    isCancelInProgress.current = true;
    setCancelling(true);

    try {
      const response = await appointmentService.cancel(selectedAppointment.id);
      if (response?.data?.success === false) {
        throw new Error(response?.data?.message || 'Failed to cancel');
      }

      setAppointments((prev) =>
        prev.map((apt) =>
          apt.id === selectedAppointment.id
            ? { ...apt, state: 'cancel', status: 'cancel', cancel: 1 }
            : apt
        )
      );

      toast.success(lang === 'ar' ? 'تم إلغاء الموعد بنجاح' : 'Appointment cancelled successfully');
      setCancelDialogOpen(false);
      setSelectedAppointment(null);
    } catch (error) {
      const status = error.response?.status;
      const errorMsg = error.response?.data?.message || error.message;

      toast.error(
        status === 429
          ? lang === 'ar'
            ? 'الرجاء الانتظار قبل المحاولة مرة أخرى'
            : 'Please wait before trying again'
          : lang === 'ar'
            ? errorMsg || 'فشل في إلغاء الموعد'
            : errorMsg || 'Failed to cancel appointment'
      );
    } finally {
      setCancelling(false);
      setTimeout(() => {
        isCancelInProgress.current = false;
      }, 2000);
    }
  };

  const handleReschedule = (appointment) => {
    if (appointment.doctor?.id) {
      navigate(`/doctors/${appointment.doctor.id}?reschedule=${appointment.id}`);
      return;
    }

    toast.error(lang === 'ar' ? 'لا يمكن إعادة الجدولة' : 'Cannot reschedule this appointment');
  };

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/login');
    }
  }, [authLoading, isAuthenticated, navigate]);

  useEffect(() => {
    const fetchAppointments = async () => {
      setLoading(true);

      try {
        const response = await appointmentService.getAllAppointments();

        if (response?.data?.exception || response?.data?.message?.includes('Undefined array key')) {
          setAppointments([]);
          return;
        }

        const data = response?.data?.data || response?.data;
        const rawAppointments = Array.isArray(data) ? data : [];

        const transformedAppointments = rawAppointments.map((apt) => {
          const state = normalizeAppointmentState(apt.state, apt.appointment_status_id);

          return {
            id: apt.id,
            doctor: {
              id: apt.doctor?.id || apt.doctor_id,
              name: getLocalizedValue(apt.doctor?.name, lang) || apt.doctor_name,
              image: normalizeImageValue(apt.doctor?.image || apt.doctor_image || apt.doctor?.image_url || apt.doctor?.media?.[0]?.url || apt.doctor?.media?.[0]?.thumb || apt.doctor?.media?.[0]?.icon || ''),
              gender: apt.doctor?.gender || apt.doctor?.sex || '',
              specialty: apt.doctor?.specialties?.[0]
                ? {
                    name: getLocalizedValue(apt.doctor.specialties[0].name, lang),
                  }
                : null,
            },
            clinic: apt.clinic || apt.clinic_name || apt.address
              ? {
                  id: apt.clinic?.id,
                  name: getLocalizedValue(apt.clinic?.name, lang) || apt.clinic_name,
                  phone: apt.clinic?.phone_number,
                  address: apt.address?.address || apt.clinic_address || '',
                }
              : null,
            patient: apt.patient
              ? {
                  name:
                    `${getLocalizedValue(apt.patient.first_name, lang)} ${getLocalizedValue(apt.patient.last_name, lang)}`.trim() ||
                    getLocalizedValue(apt.patient.name, lang),
                }
              : null,
            appointment_at: apt.appointment_at || apt.appointment_date || apt.start_at,
            start_at: apt.start_at,
            ends_at: apt.ends_at || apt.end_datetime,
            appointment_type: apt.appointment_type || apt.type || (apt.online ? 'online' : 'regular'),
            service_name: getLocalizedValue(apt.doctor_service?.name, lang) || apt.service_name,
            state,
            online: !!apt.online,
            total: apt.total ?? apt.price,
            cancel: apt.cancel,
            notes: apt.notes,
            queue_display_label: apt.queue_display_label,
            remaining_turns_count: apt.remaining_turns_count ?? apt.remaining_queue_turns,
            estimated_wait_time: apt.estimated_wait_time ?? apt.estimated_turn_arrival_minutes,
            show_queue_number_to_patient: !!apt.show_queue_number_to_patient,
            show_remaining_turns_to_patient: !!apt.show_remaining_turns_to_patient,
            show_estimated_turn_time_to_patient: !!apt.show_estimated_turn_time_to_patient,
          };
        });

        setAppointments(transformedAppointments);
      } catch (error) {
        console.error('Failed to fetch appointments:', error);
        setAppointments([]);
      } finally {
        setLoading(false);
      }
    };

    if (isAuthenticated && !authLoading) {
      fetchAppointments();
    }
  }, [isAuthenticated, authLoading, lang, realtimeRefreshKey]);

  useEffect(() => {
    const refresh = () => setRealtimeRefreshKey((value) => value + 1);
    window.addEventListener('dactrha:appointment-changed', refresh);
    const interval = window.setInterval(refresh, 30000);
    return () => {
      window.removeEventListener('dactrha:appointment-changed', refresh);
      window.clearInterval(interval);
    };
  }, []);

  const filteredAppointments = useMemo(() => {
    const items =
      activeFilter === 'all'
        ? appointments
        : appointments.filter((appointment) => normalizeAppointmentState(appointment.state) === activeFilter);

    return [...items].sort(
      (a, b) =>
        (parseApiDateTime(b.appointment_at)?.getTime() || 0) -
        (parseApiDateTime(a.appointment_at)?.getTime() || 0)
    );
  }, [activeFilter, appointments]);

  const formatAppointmentDate = (dateStr, pattern, fallback = '-') => {
    const date = parseApiDateTime(dateStr);
    return date ? format(date, pattern, { locale }) : fallback;
  };
  const formatDate = (dateStr) => formatAppointmentDate(dateStr, 'EEE, MMM d, yyyy');
  const formatTime = (dateStr) => formatAppointmentDate(dateStr, 'hh:mm a', '--:--');
  const formatDay = (dateStr) => formatAppointmentDate(dateStr, 'dd', '--');
  const formatMonth = (dateStr) => formatAppointmentDate(dateStr, 'MMM', '');

  const filterLabel = (filter) => {
    const labels = {
      all: lang === 'ar' ? 'الكل' : 'All',
      pending: lang === 'ar' ? 'قيد الانتظار' : 'Pending',
      accepted: lang === 'ar' ? 'مقبول' : 'Accepted',
      in_clinic: lang === 'ar' ? 'داخل العيادة' : 'In The Clinic',
      done: lang === 'ar' ? 'منتهي' : 'Done',
      cancel: lang === 'ar' ? 'ملغي' : 'Cancelled',
      unfinished: lang === 'ar' ? 'غير مكتمل' : 'Unfinished',
    };

    return labels[filter] || filter;
  };

  const emptyTitle =
    activeFilter === 'all'
      ? lang === 'ar'
        ? 'لا توجد مواعيد بعد'
        : 'No appointments yet'
      : lang === 'ar'
        ? 'لا توجد مواعيد بهذه الحالة'
        : 'No appointments in this status';

  const emptySubtitle =
    activeFilter === 'all'
      ? lang === 'ar'
        ? 'احجز موعدك الأول مع الطبيب'
        : 'Book your first appointment with a doctor'
      : lang === 'ar'
        ? 'جرّب حالة أخرى لعرض مواعيدك'
        : 'Try another status to view your appointments';

  const buildQueueMetaLines = (appointment) => {
    const lines = [];

    if (appointment.show_queue_number_to_patient && appointment.queue_display_label) {
      lines.push(`${lang === 'ar' ? 'الدور' : 'Queue'}: ${appointment.queue_display_label}`);
    }
    if (appointment.show_remaining_turns_to_patient && appointment.remaining_turns_count != null) {
      lines.push(
        `${lang === 'ar' ? 'الأدوار المتبقية' : 'Remaining Turns'}: ${appointment.remaining_turns_count}`
      );
    }
    if (appointment.show_estimated_turn_time_to_patient && appointment.estimated_wait_time != null) {
      lines.push(
        `${lang === 'ar' ? 'الوقت المتوقع للدور' : 'Estimated Turn Time'}: ${appointment.estimated_wait_time} ${
          lang === 'ar' ? 'دقيقة' : 'Minutes'
        }`
      );
    }

    return lines;
  };

  const getAppointmentTypeLabel = (appointment) => {
    if (appointment.service_name) return appointment.service_name;

    const type = String(appointment.appointment_type || '').trim().toLowerCase();
    const map = {
      regular: lang === 'ar' ? 'عادي' : 'Regular',
      clinic: lang === 'ar' ? 'عادي' : 'Regular',
      online: lang === 'ar' ? 'استشارة أونلاين' : 'Online consultation',
      waiting: lang === 'ar' ? 'انتظار' : 'Waiting',
      address: lang === 'ar' ? 'زيارة منزلية' : 'Home visit',
    };

    return map[type] || appointment.appointment_type || (lang === 'ar' ? 'موعد' : 'Appointment');
  };

  const AppointmentCard = ({ appointment }) => {
    const presentation = getStatusPresentation(appointment.state, lang);
    const queueMetaLines = buildQueueMetaLines(appointment);
    const appointmentTypeLabel = getAppointmentTypeLabel(appointment);

    return (
      <Card
        className="theme-panel overflow-hidden transition hover:border-slate-300/90 hover:bg-white hover:shadow-[0_28px_90px_rgba(15,23,42,0.16)] dark:hover:border-white/20 dark:hover:bg-white/[0.07] dark:hover:shadow-[0_28px_90px_rgba(15,23,42,0.3)]"
        role="button"
        tabIndex={0}
        onClick={() => navigate(`/appointments/${appointment.id}`, { state: { appointment } })}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            navigate(`/appointments/${appointment.id}`, { state: { appointment } });
          }
        }}
      >
        <CardContent className="p-4 sm:p-5">
          <div className="flex flex-col gap-4 sm:flex-row">
            <div className={`w-full rounded-[22px] border border-slate-200/80 p-4 dark:border-white/8 sm:w-[108px] ${presentation.panel}`}>
              <div className={`text-center text-sm font-semibold ${presentation.tint}`}>{formatTime(appointment.appointment_at)}</div>
              <div className={`text-center text-5xl font-black leading-none tracking-tight ${presentation.tint}`}>
                {formatDay(appointment.appointment_at)}
              </div>
              <div className={`text-center text-lg font-bold uppercase ${presentation.tint}`}>
                {formatMonth(appointment.appointment_at)}
              </div>
            </div>

            <div className="min-w-0 flex-1">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div className="min-w-0">
                  <h3 className="truncate text-2xl font-bold text-slate-900 dark:text-white">
                    {appointment.doctor?.name || (lang === 'ar' ? 'دكتور' : 'Doctor')}
                  </h3>
                  <p className="mt-1 text-base text-slate-600 dark:text-slate-300">{appointmentTypeLabel}</p>
                </div>

                <div className={`inline-flex w-fit rounded-full px-4 py-1.5 text-sm font-bold ${presentation.badge}`}>
                  {presentation.label}
                </div>
              </div>

              <div className="my-4 h-px bg-slate-200 dark:bg-white/10" />

              <div className="grid gap-3 text-base text-slate-700 dark:text-slate-200 sm:grid-cols-2">
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-slate-400" />
                  <span>{formatDate(appointment.appointment_at)}</span>
                </div>

                {appointment.clinic?.name && (
                  <div className="flex items-center gap-2">
                    <MapPin className="h-4 w-4 text-slate-400" />
                    <span className="truncate">{appointment.clinic.name}</span>
                  </div>
                )}

                {appointment.clinic?.phone && (
                  <div className="flex items-center gap-2">
                    <Phone className="h-4 w-4 text-slate-400" />
                    <span dir="ltr">{appointment.clinic.phone}</span>
                  </div>
                )}

                {appointment.online && (
                  <div className="flex items-center gap-2 text-sky-200">
                    <Video className="h-4 w-4" />
                    <span>{lang === 'ar' ? 'استشارة عبر الإنترنت' : 'Online Consultation'}</span>
                  </div>
                )}
              </div>

              {queueMetaLines.length > 0 && (
                <div className={`mt-4 rounded-2xl p-4 ${presentation.panel}`}>
                  {queueMetaLines.map((line) => (
                    <p key={line} className={`text-base font-bold ${presentation.tint}`}>
                      {line}
                    </p>
                  ))}
                </div>
              )}

              {appointment.notes?.trim() && (
                <div className="mt-4 flex items-start gap-2 text-sm text-slate-600 dark:text-slate-300">
                  <NotebookPen className="mt-0.5 h-4 w-4 text-slate-500 dark:text-slate-400" />
                  <p>{appointment.notes}</p>
                </div>
              )}

              <div className="mt-5 flex flex-col gap-2 sm:flex-row">
                {appointment.online && canJoinCall(appointment) && (
                  <Button
                    className="bg-sky-600 text-white hover:bg-sky-500"
                    onClick={(event) => {
                      event.stopPropagation();
                      handleJoinCall(appointment);
                    }}
                  >
                    <Video className="me-2 h-4 w-4" />
                    {lang === 'ar' ? 'انضم للمكالمة الآن' : 'Join Video Call Now'}
                  </Button>
                )}

                {!['cancel', 'done'].includes(normalizeAppointmentState(appointment.state)) && (
                  <>
                    {normalizeAppointmentState(appointment.state) !== 'unfinished' && (
                      <Button
                        variant="outline"
                        className="border-rose-200 bg-rose-50 font-bold text-rose-600 hover:border-rose-300 hover:bg-rose-100 hover:text-rose-700 dark:border-rose-400/25 dark:bg-rose-500/5 dark:text-rose-200 dark:hover:bg-rose-500/10 dark:hover:text-rose-100"
                        onClick={(event) => {
                          event.stopPropagation();
                          handleCancelClick(appointment);
                        }}
                      >
                        {t('appointments.cancelAppointment')}
                      </Button>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  if (!isAuthenticated) return null;

  return (
    <div className="theme-shell min-h-screen" data-testid="appointments-page">
      <section className="theme-hero">
        <div className="container mx-auto px-4 py-8 sm:py-10">
          <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <h1 className="text-3xl font-black tracking-tight text-slate-900 dark:text-white sm:text-4xl">{t('appointments.title')}</h1>
              <p className="mt-2 max-w-2xl text-base text-slate-600 dark:text-slate-300">{t('appointments.subtitle')}</p>
            </div>

            <Button className="h-12 rounded-xl bg-[#2f6fed] px-6 text-base font-bold hover:bg-[#255fd0]" asChild>
              <Link to="/doctors">{t('appointments.bookNew')}</Link>
            </Button>
          </div>
        </div>
      </section>

      <section className="container mx-auto px-4 py-6 sm:py-8">
        <div className="theme-panel-soft rounded-[28px] p-2">
          <div className="flex gap-2 overflow-x-auto pb-1">
            {FILTERS.map((filter) => {
              const selected = activeFilter === filter;
              return (
                <button
                  key={filter}
                  type="button"
                  onClick={() => setActiveFilter(filter)}
                  className={`min-w-fit rounded-2xl px-5 py-3 text-sm font-bold transition ${
                    selected
                      ? 'bg-[#5f71d8] text-white shadow-[0_10px_30px_rgba(95,113,216,0.35)]'
                      : 'bg-white/70 text-slate-600 hover:bg-white hover:text-slate-900 dark:bg-white/5 dark:text-slate-300 dark:hover:bg-white/10 dark:hover:text-white'
                  }`}
                  data-testid={`filter-${filter}`}
                >
                  {filterLabel(filter)}
                </button>
              );
            })}
          </div>
        </div>

        <div className="mt-6 space-y-4">
          {loading ? (
            [1, 2, 3].map((item) => (
              <Card key={item} className="theme-panel">
                <CardContent className="p-5">
                  <div className="flex gap-4">
                    <Skeleton className="h-28 w-24 rounded-2xl bg-slate-200 dark:bg-white/10" />
                    <div className="flex-1 space-y-3">
                      <Skeleton className="h-7 w-56 bg-slate-200 dark:bg-white/10" />
                      <Skeleton className="h-5 w-40 bg-slate-200 dark:bg-white/10" />
                      <Skeleton className="h-5 w-72 bg-slate-200 dark:bg-white/10" />
                      <Skeleton className="h-24 w-full rounded-2xl bg-slate-200 dark:bg-white/10" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          ) : filteredAppointments.length === 0 ? (
            <Card className="theme-panel">
              <CardContent className="flex min-h-[320px] flex-col items-center justify-center p-8 text-center">
                <div className="rounded-full bg-slate-100 p-5 dark:bg-white/6">
                  <Calendar className="h-12 w-12 text-slate-500 dark:text-slate-300" />
                </div>
                <h2 className="mt-5 text-2xl font-bold text-slate-900 dark:text-white">{emptyTitle}</h2>
                <p className="mt-2 max-w-md text-slate-600 dark:text-slate-300">{emptySubtitle}</p>
                <Button className="mt-6 bg-[#2f6fed] text-white hover:bg-[#255fd0]" asChild>
                  <Link to="/doctors">{t('appointments.bookNew')}</Link>
                </Button>
              </CardContent>
            </Card>
          ) : (
            filteredAppointments.map((appointment) => (
              <AppointmentCard key={appointment.id} appointment={appointment} />
            ))
          )}
        </div>
      </section>

      <Dialog open={cancelDialogOpen} onOpenChange={setCancelDialogOpen}>
        <DialogContent className="border-slate-200 bg-white text-slate-900 dark:border-white/10 dark:bg-slate-900 dark:text-white">
          <DialogHeader>
            <DialogTitle>{lang === 'ar' ? 'إلغاء الموعد' : 'Cancel Appointment'}</DialogTitle>
            <DialogDescription className="text-slate-500 dark:text-slate-400">
              {lang === 'ar'
                ? 'هل أنت متأكد من رغبتك في إلغاء هذا الموعد؟ لا يمكن التراجع عن هذا الإجراء.'
                : 'Are you sure you want to cancel this appointment? This action cannot be undone.'}
            </DialogDescription>
          </DialogHeader>

          {selectedAppointment && (
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 dark:border-white/10 dark:bg-white/5">
              <p className="text-lg font-bold">{selectedAppointment.doctor?.name}</p>
              <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                {formatDate(selectedAppointment.appointment_at)} - {formatTime(selectedAppointment.appointment_at)}
              </p>
            </div>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setCancelDialogOpen(false)}
              disabled={cancelling}
              className="theme-outline-button"
            >
              {lang === 'ar' ? 'تراجع' : 'Keep Appointment'}
            </Button>
            <Button variant="destructive" onClick={handleConfirmCancel} disabled={cancelling}>
              {cancelling ? (
                <>
                  <Loader2 className="me-2 h-4 w-4 animate-spin" />
                  {lang === 'ar' ? 'جاري الإلغاء...' : 'Cancelling...'}
                </>
              ) : (
                lang === 'ar' ? 'إلغاء الموعد' : 'Yes, Cancel'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AppointmentsPage;
